// Path: src/utils/razorpay.js

// --- tiny helpers ------------------------------------------------------------
const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const TAG_ID = "razorpay-checkout-js";

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";
const hasRazorpay = () => isBrowser() && !!window.Razorpay;

let _loadPromise = null;

// Add fast-path connection hints (idempotent)
function addHint(rel, href, attrs = {}) {
  if (!isBrowser()) return;
  const id = `rzp-hint-${rel}-${href}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = rel;
  link.href = href;
  Object.entries(attrs).forEach(([k, v]) => {
    if (v != null) link.setAttribute(k, String(v));
  });
  document.head.appendChild(link);
}

function addConnectionHints() {
  // DNS + preconnect usually shave off ~100ms on first open
  addHint("dns-prefetch", "https://checkout.razorpay.com");
  addHint("preconnect", "https://checkout.razorpay.com", { crossorigin: "anonymous" });
}

// --- public helpers ----------------------------------------------------------
export function isRazorpayLoaded() {
  return hasRazorpay();
}

/**
 * Preconnect to Razorpay early (safe to call anytime).
 * You can call this once in App/Layout mount to warm the connection.
 */
export function preconnectRazorpay() {
  addConnectionHints();
}

/**
 * Optionally warm-load the script when the browser is idle.
 * Call once on a non-donation page to improve perceived speed.
 */
export function prewarmRazorpayOnIdle(options) {
  if (!isBrowser()) return;
  const run = () => loadRazorpayScript(options).catch(() => {});
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1000);
  }
}

/**
 * Loads Razorpay checkout.js exactly once.
 * Resolves to true on success, false on failure/timeout.
 *
 * @param {{ nonce?: string, timeout?: number, src?: string }} options
 */
export function loadRazorpayScript(options = {}) {
  if (!isBrowser()) return Promise.resolve(false);
  if (hasRazorpay()) return Promise.resolve(true);
  if (_loadPromise) return _loadPromise;

  const { nonce, timeout = 15000, src = RZP_SRC } = options;

  addConnectionHints();

  _loadPromise = new Promise((resolve) => {
    // If a tag exists, bind to its events / state
    const existing = document.getElementById(TAG_ID);
    if (existing) {
      const done = () => resolve(hasRazorpay());
      if (existing.getAttribute("data-loaded") === "1") return done();

      existing.addEventListener("load", () => {
        existing.setAttribute("data-loaded", "1");
        done();
      });
      existing.addEventListener("error", () => resolve(false));
    } else {
      // Create the script tag
      const s = document.createElement("script");
      s.id = TAG_ID;
      s.src = src;
      s.async = true;
      s.crossOrigin = "anonymous";
      if (nonce) s.nonce = nonce;

      s.onload = () => {
        s.setAttribute("data-loaded", "1");
        resolve(hasRazorpay());
      };
      s.onerror = () => {
        // Remove bad tag so a later retry can succeed
        try { s.remove(); } catch {}
        _loadPromise = null;
        resolve(false);
      };

      document.head.appendChild(s);
    }

    // Safety timeout (script blocked by extensions, etc.)
    const t = setTimeout(() => {
      if (!hasRazorpay()) {
        _loadPromise = null;
        resolve(false);
      }
    }, timeout);

    // If we resolved early, clear the timeout
    _loadPromise?.finally?.(() => clearTimeout(t));
  });

  // If load ultimately failed, allow retries later
  return _loadPromise.finally(() => {
    if (!hasRazorpay()) _loadPromise = null;
  });
}

/**
 * Convenience: makes sure Razorpay is ready and returns the constructor.
 * @throws on failure
 */
export async function ensureRazorpay(options) {
  const ok = await loadRazorpayScript(options);
  if (!ok) throw new Error("Failed to load Razorpay");
  return window.Razorpay;
}
